document.addEventListener("alpine:init", () => {
  Alpine.data("addProductForm", () => ({
    addToCartHook: null, // Parent elements can set hook function and return true to prevent default addToCart action
    userLat: null,
    userLon: null,
    context: Alpine.store("pdp"),
    product: null,
    form: null,
    submitBtn: null,
    submitText: null,
    loadingSpinner: null,
    notifyDialog: null,
    notifyForm: null,
    specialtyTagMessage: null,
    specialtyDiscounts: window.specialtyDiscounts,
    productDetailsSubscription: null,
    subscriptionFrequencyLengthSelect: null,
    sellingPlanRadio: null,
    productSellingPlanGroups: window.__addProductFormData.sellingPlanGroups,
    discountPrice: null,
    comparePrice: null,

    async init() {
      // Always set up essential DOM refs first, regardless of product type
      this.form = this.$el.querySelector("form");
      this.submitBtn = this.$refs.submit;
      this.submitText = this.$refs.submitText;
      this.loadingSpinner = this.$refs.loadingSpinner;
      if (!this.loadingSpinner) {
        this.loadingSpinner = this.$el.querySelector('[x-ref="loadingSpinner"]');
      }

      // While the customer hasn't picked every option, force the submit
      // button into a disabled "Select a [Color] and [Size]" state. Seed
      // selectionMissing synchronously from the Liquid-rendered data
      // attribute so handleVariantIdChange (fires immediately via the
      // Alpine.effect below) sees it before deciding what to render.
      const initialMissingAttr = this.$el.getAttribute("data-initial-missing-options") || "";
      this.selectionMissing = initialMissingAttr
        ? initialMissingAttr
            .split(" and ")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      this.initialMissing = [...this.selectionMissing];
      window.addEventListener("pdp:selection-change", () => {
        // Recompute from DOM state regardless of init status — pre-init
        // clicks must still take effect, and a stale selectionMissing here
        // would cause handleVariantIdChange to short-circuit on the click
        // that completes the selection.
        this.selectionMissing = this.initialMissing.filter((optName) => {
          const group = document.querySelector(`[data-option-group][data-option-name="${optName}"]`);
          return !group?.querySelector('[data-option-btn][aria-pressed="true"]');
        });
        this.applySelectionMissingOverride();
        // If everything's selected, enable ATC directly. handleVariantIdChange
        // may further refine state (OOS, backorder, etc.) but should never
        // be the only path that flips the button to enabled.
        if (this.selectionMissing.length === 0) this.clearNeedsSelection();
        if (this.selectionMissing.length === 0 && this.submitBtn && !this.submitBtn.hasAttribute("data-b2b-disabled")) {
          this.submitBtn.disabled = false;
          if (this.submitText && /Select a /i.test(this.submitText.textContent || "")) {
            this.submitText.textContent = this.addLabel();
          }
        }
      });
      // Inventory loads asynchronously; once it arrives the set of sold-out
      // variants can change, so re-evaluate the "Notify Me" fallback for any
      // still-incomplete selection.
      window.addEventListener("pdp:inventory-loaded", () => {
        this.applySelectionMissingOverride();
      });

      this.isBundle = this.$el.getAttribute("data-custom-bundle") == "true";
      if (this.isBundle) {
        // Disable ATC if any bundle product's selected variant is unavailable
        const updateBundleATC = () => {
          const avail = window.selectedVariantAvailability || {};
          const allAvailable = Object.values(avail).every(Boolean);
          this.submitBtn.disabled = !allAvailable;
          if (this.submitText) {
            this.submitText.textContent = allAvailable ? this.addLabel() : "Unavailable";
          }
        };
        window.addEventListener("selected-variants-updated", updateBundleATC);
        updateBundleATC();

        return;
      }

      Alpine.reactive(this.context);
      Alpine.effect(() => {
        const { variantId, product } = this.context;
        // Re-run button state when BOPIS mode or the selected pickup store changes,
        // so the label and availability stay in sync with the selector swatches.
        void this.context.bopisMode;
        void this.context.bopisStore;
        const variant = product.variants.find((v) => String(v.id) === String(variantId));
        const discountPrice = this.context.getVariantDiscountPrice(variant);
        this.discountPrice = this.context.formatMoney(discountPrice);
        this.comparePrice = this.context.formatMoney(variant.compare_at_price ?? variant.price);
        const imgData = this.context.featuredImageUrl(product, variant);
        const imgSrc = imgData?.src ?? (typeof imgData === "string" ? imgData : null);
        if (imgSrc) {
          const url = new URL(imgSrc.startsWith("//") ? window.location.protocol + imgSrc : imgSrc);
          url.searchParams.set("width", "100");
          this.$el.style.setProperty("--media-url", `url(${url.href})`);
        }
        if (!this.form) return;
        this.handleVariantIdChange();
        const { addonLineItems } = this.context;
        if (!addonLineItems) return;
        if (addonLineItems.length) {
          this.addToCartHook = async () => {
            await fetch(Shopify.routes.root + "cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ items: addonLineItems }),
            });
          };
        } else {
          this.addToCartHook = null;
        }
      });
      // Wire up remaining refs
      this.notifyDialog = this.$refs.notifyDialog;
      this.notifyForm = this.$refs.notifyForm;

      this.scrolledPastObserver();

      // Specialty tag moved to the selectors component — look it up globally
      // by id rather than via $refs. May be null if product has no Size option.
      this.specialtyTagMessage = document.getElementById("specialty-tag");
      this.productDetailsSubscription = this.$refs.productDetailsSubscription;
      this.subscriptionFrequencyLengthSelect = this.productDetailsSubscription?.querySelector(
        "#subscriptionFrequencyLengthSelect"
      );
      this.sellingPlanRadio = this.productDetailsSubscription?.querySelector("#sellingPlanRadio");

      const { variantId, product } = this.context;
      this.product = product;
      // Subscriptions
      this.subscriptionFrequencyLengthSelect?.addEventListener("change", () => {
        if (!this.sellingPlanRadio) return;
        this.sellingPlanRadio.value = this.subscriptionFrequencyLengthSelect.value;
      });
      try {
        const res = await fetch("https://cozy-earth-api.fly.dev/v1/inventory-levels?productId=" + this.product.id, {
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) throw new Error("inventory-levels HTTP " + res.status);

        const data = await res.json();

        // Attach inventory levels to the product
        this.product.inventoryLevels = {};
        data.forEach((variant) => {
          this.product.inventoryLevels[variant.variantId] = {
            inventory: variant.inventory,
            totalAvailable: variant.totalAvailable,
            distances: {},
          };
        });
      } catch (err) {
        console.error("Error fetching inventory levels:", err);
      }

      let { attributes } = await this.fetchCart();
      attributes = attributes || {};

      let latitude = attributes.user_latitude;
      let longitude = attributes.user_longitude;
      if (latitude) latitude = parseFloat(latitude).toFixed(2);
      if (longitude) longitude = parseFloat(longitude).toFixed(2);

      if (!latitude || !longitude) {
        try {
          const res = await fetch("https://cozy-earth-api.fly.dev/v1/geoip");
          if (!res.ok) throw new Error("geoip HTTP " + res.status);
          const data = await res.json();

          if (typeof data.latitude !== "number" || typeof data.longitude !== "number") {
            console.error("Invalid geolocation data:", data);
          } else {
            latitude = Number(data.latitude).toFixed(2);
            longitude = Number(data.longitude).toFixed(2);
            console.log("Fetched geolocation data:", { latitude, longitude });
          }
        } catch (err) {
          console.error("Failed to fetch geolocation data:", err);
        }
      }

      if (latitude && longitude) {
        this.userLat = Number(parseFloat(latitude).toFixed(2));
        this.userLon = Number(parseFloat(longitude).toFixed(2));

        Alpine.store("pdp").userLocation = {
          latitude: this.userLat,
          longitude: this.userLon,
        };
      }

      this.initComplete = true;
      // Signals inventory is ready — BOPIS banners gate on this to avoid a stock-copy flash.
      this.context.inventoryLoaded = true;
      // Re-evaluate selections the user may have made before init finished.
      window.dispatchEvent(new CustomEvent("pdp:selection-change", { detail: {} }));
      this.handleVariantIdChange();
      window.dispatchEvent(new CustomEvent("pdp:inventory-loaded"));
      // QA #7: honor ?selling_plan= from the cart link (after the variant's
      // frequency options have been built above).
      this.applyInitialSellingPlanFromUrl();
    },
    async fetchCart() {
      const res = await fetch(window.Shopify.routes.root + "cart.js");
      return await res.json();
    },
    async handleSubmit() {
      // Options still missing: the button is only "semi-clickable" — point
      // the customer at what they still need to pick instead of submitting.
      if (this.submitBtn?.hasAttribute("data-needs-selection")) {
        window.dispatchEvent(
          new CustomEvent("pdp:selection-required", { detail: { missing: [...(this.selectionMissing || [])] } })
        );
        return;
      }
      if (this.isBundle) {
        this.addBundleToCart();
        return;
      }
      const variantId = this.context?.variantId;
      const selectedVariant = this.context?.product?.variants?.find((v) => v.id === variantId);

      // If product data or variant not available (e.g. candle PDPs), just add to cart directly
      if (!this.product || !selectedVariant) {
        this.addToCart();
        return;
      }

      const specialtyRulingValidated = await this.validateSpecialtyDiscounts();
      if (!specialtyRulingValidated) {
        this.showValidationError();
        return;
      } else if (selectedVariant.available) {
        let pickupAvailable = false;
        let shippingAvailable = false;

        if (this.context?.bopisMode) {
          // BOPIS: only the selected pickup store's stock can fulfill the order —
          // online availability must not let an out-of-store variant through.
          pickupAvailable = this.context.isVariantPickupAvailable(selectedVariant.id);
          shippingAvailable = false;
        } else {
          const levels = this.product.inventoryLevels?.[selectedVariant.id];

          if (!levels || !levels.inventory) {
            pickupAvailable = true;
            shippingAvailable = true;
          } else {
            for (const [storeName, inventoryLevel] of Object.entries(levels.inventory)) {
              const distance = levels.distances?.[storeName];

              if (storeName === "Cozy Earth Online" && inventoryLevel > 0) {
                shippingAvailable = true;
              }

              if (inventoryLevel > 0 && distance <= 50) {
                pickupAvailable = true;
                break;
              }
            }
          }
        }

        if (shippingAvailable || pickupAvailable) {
          this.addToCart();
        } else if (!this.context?.bopisMode) {
          this.notifyDialog?.toggleAttribute("open", true);
        }
        // BOPIS + unavailable: no action (button is disabled / out of stock).
      } else if (selectedVariant && !this.context?.bopisMode) {
        this.notifyDialog?.toggleAttribute("open", true);
      }
    },
    async addToCart() {
      const variantId = this.context?.variantId;
      const variant = this.product?.variants?.find((v) => v.id === variantId);
      if (!this.loadingSpinner) {
        console.error("Cannot set loading state: loadingSpinner ref is missing");
      } else {
        this.loadingSpinner.toggleAttribute("loading", true);
      }
      try {
        const { action, method } = this.form;
        const body = new FormData(this.form);
        // BOPIS: tag the line with its pickup store so checkout can default to "Pick up".
        const bopisStore = this.context?.bopisMode ? this.context.bopisStore : null;
        const pickupLocationId = window.PickupPreference?.locationIdFor(bopisStore);
        if (pickupLocationId) {
          body.append("properties[_pickup_location]", pickupLocationId);
          window.PickupPreference.claimGeoAttributes();
        }
        if (await this.addToCartHook?.(body)) throw "Returning early from addToCart hook";
        const res = await fetch(action, {
          method,
          headers: {
            Accept: "application/json",
          },
          body,
        });
        if (res.ok) {
          const ld = this.lightningDeal;
          const isLdVariant = ld?.variants?.length > 0 ? ld.variants.includes(variant.id) : true;

          const now = new Date();
          const endDate = new Date(ld?.end_datetime);

          const isValidLD =
            ld && isLdVariant && ld.start_datetime && now < endDate && ld.quantity_sold < ld.sell_quantity;

          const lineItem = await res.json();
          // Fire-and-forget: a failed preference update must never fail the add.
          if (pickupLocationId) window.PickupPreference.applyPickup(bopisStore);
          this.dispatchComposedEvent("cart:change", { lineItem });
          this.dispatchComposedEvent("cartItemPopup:open", {
            lineItemKeys: [lineItem.key],
          });
          const weMadeTooMuch =
            Number(this.product?.variantIdMetafieldMap?.[variant?.id]?.["udundi.we_made_too_much"]) || 0;
          if (weMadeTooMuch > 0 || isValidLD) {
            this.dispatchComposedEvent("wmtmModal:open");
          }
          window.compose?.dispatchEvent(new CustomEvent("goal:add-to-cart"));
        } else {
          let data;
          try {
            data = await res.json();
          } finally {
            throw new Error(data?.errors || "Failed to add item to cart");
          }
        }
      } finally {
        this.loadingSpinner?.toggleAttribute("loading", false);
      }
    },
    async addBundleToCart() {
      const entries = Object.entries(window.selectedVariants || {});
      if (!entries.length) {
        alert("No variants selected.");
        return;
      }

      this.loadingSpinner?.toggleAttribute("loading", true);

      try {
        const items = entries.map(([productId, variantId]) => ({
          id: variantId,
          quantity: this.quantity || 1,
          properties: { _custom_bundle: "true" },
        }));

        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ items }),
        });

        if (res.ok) {
          const cartUpdate = await res.json();
          this.dispatchComposedEvent("cart:change", { cartUpdate });
          window.compose?.dispatchEvent(new CustomEvent("goal:add-to-cart"));
          window.location.hash = "#dialog-cart";
        } else {
          const data = await res.json();
          throw new Error(data?.description || "Failed to add items to cart.");
        }
      } catch (error) {
        console.error("Error adding bundle to cart:", error);
        alert("Error adding bundle to cart");
      } finally {
        this.loadingSpinner?.toggleAttribute("loading", false);
      }
    },
    async addBundleToCart() {
      const entries = Object.entries(window.selectedVariants || {});
      if (!entries.length) {
        alert("No variants selected.");
        return;
      }

      this.loadingSpinner?.toggleAttribute("loading", true);

      try {
        const items = entries.map(([productId, variantId]) => ({
          id: variantId,
          quantity: this.quantity || 1,
          properties: { _custom_bundle: "true" },
        }));

        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ items }),
        });

        if (res.ok) {
          const cartUpdate = await res.json();
          this.dispatchComposedEvent("cart:change", { cartUpdate });
          window.compose?.dispatchEvent(new CustomEvent("goal:add-to-cart"));
          window.location.hash = "#dialog-cart";
        } else {
          const data = await res.json();
          throw new Error(data?.description || "Failed to add items to cart.");
        }
      } catch (error) {
        console.error("Error adding bundle to cart:", error);
        alert("Error adding bundle to cart");
      } finally {
        this.loadingSpinner?.toggleAttribute("loading", false);
      }
    },
    dispatchComposedEvent(name, detail) {
      window.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail }));
    },
    async validateSpecialtyDiscounts() {
      if (!this.specialtyDiscounts || this.specialtyDiscounts === "{}") return true;
      if (!this.form) return true;

      const formData = new FormData(this.form);
      if (parseInt(formData.get("quantity")) > this.specialtyDiscounts.maximum_quantity_per_variant) return false;

      const variantId = this.context?.variantId;
      const selectedVariant = this.context?.product?.variants?.find((v) => v.id === variantId);

      let totalProductCount = 0;

      const { items } = await this.fetchCart();
      return items.every(({ product_id, variant_id, quantity, discounts }, i) => {
        // Only run if discount is applied to the cart or enabled to run without discounts
        const applicable =
          this.specialtyDiscounts.enable_without_codes ||
          discounts?.find(({ title }) => this.specialtyDiscounts.codes.includes(title));

        if (!applicable) return true;

        // Accumulate the total amount of line items that come from the same product
        totalProductCount = this.product?.id === product_id ? totalProductCount + 1 : 0;

        if (
          totalProductCount >= this.specialtyDiscounts.maximum_quantity_per_product ||
          (selectedVariant?.id === variant_id && quantity >= this.specialtyDiscounts.maximum_quantity_per_variant)
        )
          return false;

        return true;
      });
    },
    showValidationError() {
      const atcButton = this.$refs.submit;
      const customerDisclaimer = document.querySelector("#specialtyRulingCustomerDisclaimer");

      atcButton.setAttribute("disabled", "true");
      atcButton.setAttribute("aria-disabled", "true");
      atcButton.classList.add("cursor-not-allowed");
      customerDisclaimer.classList.remove("text-gray-400");
      customerDisclaimer.classList.add("text-ce-red");

      setTimeout(() => {
        atcButton.removeAttribute("disabled");
        atcButton.removeAttribute("aria-disabled");
        atcButton.classList.remove("cursor-not-allowed");
        customerDisclaimer.classList.remove("text-ce-red");
        customerDisclaimer.classList.add("text-gray-400");
      }, 2000);
    },
    async handleVariantIdChange() {
      const variantId = this.context.variantId;
      const variant = this.context.product.variants.find((v) => v.id === variantId);
      if (!variant || !variant.id) {
        this.handleUndefinedVariant();
        return;
      }

      // During partial selection, every write below would be overridden
      // anyway. Skip the work (esp. the store-availability fetch) so the
      // variant-change frame is free for the media gallery's image swap.
      if ((this.selectionMissing || []).length > 0) {
        this.applySelectionMissingOverride();
        return;
      }

      const metafields = this.context.variantIdMetafieldMap[variant.id];

      this.setInput("id", variant.id);

      await fetch(
        `${window.Shopify.routes.root}products/${product.handle}?variant=${variant.id}&section_id=meta-variant-store-availability`
      )
        .then((res) => {
          if (!res.ok) throw new Error(`Store availability request failed with status ${res.status}`);
          return res.text();
        })
        .then((html) => {
          if (!html || html.trim().length === 0) {
            this.storeAvailability = {};
            return;
          }

          const doc = new DOMParser().parseFromString(html, "text/html");
          const jsonScript = doc.querySelector("#variant-store-availability");
          if (!jsonScript || !jsonScript.textContent.trim()) {
            this.storeAvailability = {};
            return;
          }
          try {
            const parsed = JSON.parse(jsonScript.textContent);
            // Shopify's empty-json pattern is { "": {} }; treat as empty.
            const keys = Object.keys(parsed);
            if (keys.length === 1 && keys[0] === "" && Object.keys(parsed[""] || {}).length === 0) {
              this.storeAvailability = {};
              return;
            }
            this.storeAvailability = parsed;
          } catch (e) {
            console.error("Failed to parse store availability JSON:", e);
            this.storeAvailability = {};
          }
        })
        .catch((err) => {
          console.error("Error fetching variant store availability:", err);
          this.storeAvailability = {};
        });

      if (this.userLat && this.userLon && this.storeAvailability && Object.keys(this.storeAvailability).length > 0) {
        Object.entries(this.storeAvailability).forEach(([variantId, stores]) => {
          if (!stores || typeof stores !== "object") return;

          Object.entries(stores).forEach(([storeName, storeData]) => {
            const storeLat = parseFloat(storeData.latitude);
            const storeLon = parseFloat(storeData.longitude);

            if (!storeLat || !storeLon) {
              console.warn(`Skipping ${storeName} - missing coordinates`);
              return;
            }

            if (!this.product.inventoryLevels) {
              return;
            }
            const distance = this.haversineDistanceMiles(this.userLat, this.userLon, storeLat, storeLon);
            if (!this.product.inventoryLevels[variantId].distances) {
              this.product.inventoryLevels[variantId].distances = {};
            }
            this.product.inventoryLevels[variantId].distances[storeName] = distance;
          });
        });
      }

      this.notifyForm.setAttribute("variant-id", variant.id);
      if (!this.submitBtn.hasAttribute("data-b2b-disabled")) {
        this.submitBtn.disabled = false;
      }
      this.submitBtn.removeEventListener("click", this.handleNotifyModal);

      // Specialty tag (lives in the size selector):
      //   "Only X left!"   — < 20 online units of the selected variant
      //   "Selling fast!"  — ≥ 20 online units AND product tagged 'Selling Fast'
      //   hidden otherwise
      if (this.specialtyTagMessage) {
        const tag = this.specialtyTagMessage;
        tag.hidden = true;
        tag.textContent = "";

        if (variant.available) {
          const onlineStock = this.product.inventoryLevels?.[variant.id]?.inventory?.["Cozy Earth Online"];

          if (typeof onlineStock === "number" && onlineStock > 0 && onlineStock < 20) {
            tag.textContent = `Only ${onlineStock} left!`;
            tag.hidden = false;
          } else if (this.product.tags.includes("Selling Fast")) {
            tag.textContent = "Selling fast!";
            tag.hidden = false;
          }
        }
      }

      const isBackorder = !variant.available && variant.inventory_policy == "continue";

      if (isBackorder) {
        this.setOutOfStock(false);
        // modify pdp-chat-button-position.css var
        document.querySelector(":root").style.setProperty("--widget-height", "70px");
        this.submitText.textContent = "Add to cart - Backorder";
        this.submitBtn.ariaLabel = "Item on backorder. Click button to add to cart and open cart";
      } else if (!variant.available && metafields["custom.is_active"] == "discontinued") {
        this.submitText.textContent = "Discontinued";
        this.setOutOfStock(true);
        this.submitBtn.disabled = true;
      } else if (variant.available) {
        let pickupAvailable = false;
        let shippingAvailable = false;

        if (this.context?.bopisMode) {
          // BOPIS: availability is the selected pickup store's stock (in sync with the
          // selector swatches). Shipping is irrelevant in pickup-only mode.
          pickupAvailable = this.context.isVariantPickupAvailable(variant.id);
          shippingAvailable = false;
        } else {
          const levels = this.product.inventoryLevels?.[variant.id];
          if (!levels || !levels.inventory) {
            pickupAvailable = true;
            shippingAvailable = true;
          } else {
            for (const [storeName, inventoryLevel] of Object.entries(
              this.product.inventoryLevels[variant.id].inventory
            )) {
              const distance = this.product.inventoryLevels[variant.id].distances[storeName];

              if (storeName == "Cozy Earth Online" && inventoryLevel > 0) {
                shippingAvailable = true;

                this.dispatchComposedEvent("shipping:available", {
                  productId: this.productId,
                  variantId: this.variantId,
                });
              }

              if (inventoryLevel > 0 && distance <= 50) {
                pickupAvailable = true;
                break;
              }
            }
          }
        }

        if (!shippingAvailable && pickupAvailable) {
          this.dispatchComposedEvent("shipping:unavailable", {
            productId: this.productId,
            variantId: this.variantId,
          });
        }

        if (!pickupAvailable && !shippingAvailable) {
          if (this.context?.bopisMode) {
            // Pickup-only: no online or notify fallback; show a disabled "Out of Stock".
            this.setBopisOutOfStock();
            this.applySelectionMissingOverride();
            return;
          }
          this.dispatchComposedEvent("shipping:available", {
            productId: this.productId,
            variantId: this.variantId,
          });
          if (this.specialtyTagMessage) this.specialtyTagMessage.hidden = true;
          this.submitText.textContent = "Out of Stock – Notify Me";
          this.setOutOfStock(true);
          this.submitBtn.ariaLabel = "Notify me when item is in stock and open out of stock dialog";
          this.submitBtn.style.backgroundColor = "#626262";
          this.submitBtn.addEventListener("click", this.handleNotifyModal);
          document.querySelector(":root").style.setProperty("--widget-height", "100px");
          this.applySelectionMissingOverride();
          return;
        }

        document.querySelector(":root").style.setProperty("--widget-height", "70px");
        this.submitText.textContent = this.addLabel();
        this.setOutOfStock(false);
        this.submitBtn.style.backgroundColor = "#13181F";
        this.submitBtn.ariaLabel = "Click button to add to cart and open cart";
      } else if (this.context?.bopisMode) {
        // Pickup-only and unavailable everywhere: disabled "Out of Stock", no notify.
        this.setBopisOutOfStock();
      } else {
        document.querySelector(":root").style.setProperty("--widget-height", "100px");
        this.submitText.textContent = "Out of Stock – Notify Me";
        this.setOutOfStock(true);
        this.submitBtn.ariaLabel = "Notify me when item is in stock and open out of stock dialoge";
        this.submitBtn.style.backgroundColor = "#626262";
        this.submitBtn.addEventListener("click", this.handleNotifyModal);
        if (this.specialtyTagMessage) this.specialtyTagMessage.hidden = true;
      }

      if (window.__addProductFormData.b2bCustomer && product.handle == "annual") {
        this.submitText.textContent = "Unavailable for B2B";
        this.submitBtn.disabled = true;
        this.submitBtn.ariaDisabled = true;
        this.submitBtn.setAttribute("data-b2b-disabled", "true");
      } else {
        this.submitBtn.removeAttribute("data-b2b-disabled");
      }

      // handle line item properties

      const ld = this.lightningDeal;
      const isLdVariant = ld?.variants?.length > 0 ? ld.variants.includes(variant.id) : true;

      const now = new Date();
      const endDate = new Date(ld?.end_datetime);

      const isValidLd = ld && isLdVariant && ld.start_datetime && now < endDate && ld.quantity_sold < ld.sell_quantity;

      const updateLineItemProperties = {
        // exclude variant from sale if product tag or variant metafield
        _exclude_variant_from_sale:
          window.testDiscount ||
          this.product.tags.includes("exclude_from_sale") ||
          metafields["udundi.exclude_from_discount"],
        // we made too much if variant metafield is greater than 0
        _we_made_too_much:
          metafields["udundi.we_made_too_much"] > 0 && window.__addProductFormData.weMadeTooMuchEnabled
            ? metafields["udundi.we_made_too_much"]
            : null,

        // LD discount
        _ld_discount: isValidLd ? ld.discount : null,

        // LD ID
        _ld_id: isValidLd ? ld.metaobject_id : null,

        _cd_deal: Array.from(this.product.tags).find((tag) => tag.startsWith("cozy_days_")) || null,
        // pre order item if variant inventory message metafield is set
        "Pre-Order": metafields["global.inventory_message"]?.length > 2 ? metafields["global.inventory_message"] : null,
      };
      for (const [name, value] of Object.entries(updateLineItemProperties)) {
        if (value !== null) {
          this.setLineItemProperty(name, value);
        } else {
          this.removeLineItemProperty(name);
        }
      }

      this.handleSellingPlanGroups();
      this.applySelectionMissingOverride();
    },
    // Single source of truth for the "no selection yet" UX. Pin the
    // submit button to disabled "Select a [Color] and [Size]" and hide
    // the specialty tag whenever options are still missing. Called
    // after every state setter so nothing can leave a stale "Add to Bag"
    // visible during partial selection.
    applySelectionMissingOverride() {
      if (!this.submitBtn || !this.submitText) return;
      const missing = this.selectionMissing || [];
      if (missing.length === 0) return;

      // The customer hasn't finished picking options. Normally we pin the
      // button to a disabled "Select a [option]" prompt. But when every
      // variant still reachable from the current (partial) selection is sold
      // out, there's no size/option they could pick that would enable Add to
      // Bag — so surface "Out of Stock – Notify Me" directly instead of
      // making them hunt through crossed-out swatches to discover it.
      if (this.selectionHasNoPurchasableVariant()) {
        this.clearNeedsSelection();
        if (this.context?.bopisMode) {
          // Pickup-only: disabled "Out of Stock" instead of the notify flow.
          this.setBopisOutOfStock();
          return;
        }
        if (!this.submitBtn.hasAttribute("data-b2b-disabled")) {
          this.submitBtn.disabled = false;
        }
        this.submitBtn.style.removeProperty("background-color");
        this.submitText.textContent = "Out of Stock – Notify Me";
        this.setOutOfStock(true);
        this.submitBtn.ariaLabel = "Notify me when item is in stock and open out of stock dialog";
        document.querySelector(":root").style.setProperty("--widget-height", "100px");
        if (this.specialtyTagMessage) {
          this.specialtyTagMessage.hidden = true;
          this.specialtyTagMessage.textContent = "";
        }
        return;
      }

      // Keep the button enabled (but styled grey via [data-needs-selection])
      // so a click can surface the "Select a size" callout. B2B stays fully
      // disabled.
      this.submitBtn.disabled = this.submitBtn.hasAttribute("data-b2b-disabled");
      this.submitBtn.setAttribute("aria-disabled", "true");
      this.submitBtn.setAttribute("data-needs-selection", "");
      this.submitBtn.style.removeProperty("background-color");
      this.submitText.textContent = `Select a ${missing.join(" and ")}`;
      this.setOutOfStock(false);
      if (this.specialtyTagMessage) {
        this.specialtyTagMessage.hidden = true;
        this.specialtyTagMessage.textContent = "";
      }
    },
    clearNeedsSelection() {
      if (!this.submitBtn?.hasAttribute("data-needs-selection")) return;
      this.submitBtn.removeAttribute("data-needs-selection");
      this.submitBtn.removeAttribute("aria-disabled");
    },
    // Label for the add button. In BOPIS mode the order is a pickup order.
    addLabel() {
      return this.context?.bopisMode ? "Add to Pickup Order" : "Add to Bag";
    },
    // Shared with the PDP store so the shipping estimate ("Order within… to get
    // it by…") can hide itself when nothing can actually ship.
    setOutOfStock(value) {
      if (this.context) this.context.outOfStock = value;
    },
    // BOPIS out-of-stock state: pickup has no online/notify fallback, so the button
    // simply reads "Out of Stock" and is disabled with no click action.
    setBopisOutOfStock() {
      if (!this.submitBtn || !this.submitText) return;
      this.submitBtn.removeEventListener("click", this.handleNotifyModal);
      this.submitText.textContent = "Out of Stock";
      this.setOutOfStock(true);
      this.submitBtn.ariaLabel = "Out of stock at this pickup store";
      this.submitBtn.style.backgroundColor = "#626262";
      this.submitBtn.disabled = true;
      this.submitBtn.ariaDisabled = true;
      if (this.specialtyTagMessage) this.specialtyTagMessage.hidden = true;
      document.querySelector(":root").style.setProperty("--widget-height", "100px");
    },
    // Mirrors the size-swatch cross-out logic in product-details-selectors.js:
    // a variant counts as purchasable when Shopify reports it available AND it has
    // online (or nearby pickup) inventory — or, in BOPIS mode, stock at the selected
    // pickup store (delegated to the pdp store so the button and swatches stay in sync).
    isVariantLocationAvailable(variantId) {
      const levels = this.product?.inventoryLevels?.[variantId];
      // Inventory data hasn't loaded yet — assume available. Re-runs on
      // pdp:inventory-loaded once levels arrive.
      if (!levels || !levels.inventory) return true;

      if (this.context?.bopisMode) {
        return this.context.isVariantPickupAvailable(variantId);
      }
      for (const [storeName, qty] of Object.entries(levels.inventory)) {
        if (storeName === "Cozy Earth Online" && qty > 0) return true;
        const dist = levels.distances?.[storeName];
        if (qty > 0 && dist != null && dist <= 50) return true;
      }
      return false;
    },
    // True when no variant consistent with the customer's current (partial)
    // option picks can be purchased — i.e. every reachable size/option is
    // sold out. Drives the "Notify Me" fallback while options are still
    // missing. Reads pressed swatches straight from the selector DOM so it
    // stays in sync with which buttons are crossed out.
    selectionHasNoPurchasableVariant() {
      const variants = this.product?.variants || [];
      if (!variants.length) return false;

      const pressed = [];
      document.querySelectorAll("[data-pdp-selectors] [data-option-group]").forEach((group) => {
        const idx = Number(group.getAttribute("data-option-index"));
        const btn = group.querySelector('[data-option-btn][aria-pressed="true"]');
        if (btn) pressed[idx] = btn.getAttribute("data-option-value");
      });

      const reachable = variants.filter((v) => {
        const opts = v.options || [];
        return pressed.every((val, i) => val == null || opts[i] === val);
      });
      if (!reachable.length) return false;

      return !reachable.some((v) => v.available !== false && this.isVariantLocationAvailable(v.id));
    },
    handleUndefinedVariant() {
      if (this.$el.getAttribute("data-custom-bundle") == "true") {
        return;
      }
      this.submitBtn.disabled = true;
      this.submitText.textContent = "Unavailable";
      this.setOutOfStock(true);
      this.setInput("id", "");
      this.notifyForm.setAttribute("variant-id", "");
      this.applySelectionMissingOverride();
    },
    setInput(name, value) {
      const existing = this.getInput(name);
      if (existing) {
        existing.value = value;
        return;
      }
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      this.form.prepend(input);
    },
    removeInput(name) {
      this.getInput(name)?.remove();
    },
    getInput(name) {
      return this.form.elements.namedItem(name);
    },
    setLineItemProperty(name, value) {
      this.setInput(`properties[${name}]`, value);
    },
    removeLineItemProperty(name) {
      this.removeInput(`properties[${name}]`);
    },
    handleSellingPlanGroups() {
      const variantId = this.context.variantId;
      const selectedVariant = this.product.variants.find((v) => v.id === variantId);

      if (!this.productDetailsSubscription || !this.productSellingPlanGroups || !this.subscriptionFrequencyLengthSelect)
        return;

      this.subscriptionFrequencyLengthSelect.innerHTML = "";

      if (selectedVariant.selling_plan_allocations.length <= 0) {
        this.sellingPlanRadio.value = "";
        this.productDetailsSubscription.hidden = true;
        return;
      }

      this.productDetailsSubscription.hidden = false;

      selectedVariant.selling_plan_allocations.forEach(({ selling_plan_id, price_adjustments }, i) => {
        const optionEl = document.createElement("option");

        if (i === 0) {
          this.sellingPlanRadio.value = selling_plan_id;
          optionEl.setAttribute("selected", "true");
        }
        optionEl.classList.add("text-sm");
        optionEl.value = selling_plan_id;

        let sellingPlan;
        this.productSellingPlanGroups.find(({ selling_plans }) => {
          sellingPlan = selling_plans.find(({ id }) => id === selling_plan_id);
          return sellingPlan;
        });
        optionEl.textContent = sellingPlan?.name;

        this.subscriptionFrequencyLengthSelect.appendChild(optionEl);
      });
    },
    // QA #7: when the shopper arrives from the cart with ?selling_plan=<id>,
    // preselect Subscribe & Save at that delivery frequency instead of the
    // default one-time purchase. Runs after init so it is the last writer,
    // and drives the same change handlers a manual selection would.
    applyInitialSellingPlanFromUrl() {
      try {
        const planId = new URLSearchParams(window.location.search).get("selling_plan");
        if (!planId) return;
        const select = this.subscriptionFrequencyLengthSelect;
        const radio = this.sellingPlanRadio;
        if (!select || !radio) return;
        const hasOption = Array.from(select.options).some((o) => String(o.value) === String(planId));
        if (!hasOption) return;
        select.value = String(planId);
        radio.value = String(planId);
        radio.checked = true; // same-name radio auto-unchecks the one-time option
        const oneTime = this.productDetailsSubscription?.querySelector("#oneTimePurchaseRadio");
        if (oneTime) oneTime.checked = false;
        // Fire change so the subscription custom element + Alpine listener sync
        // price, radio value, and frequency to the restored plan.
        select.dispatchEvent(new Event("change", { bubbles: true }));
        radio.dispatchEvent(new Event("change", { bubbles: true }));
      } catch (e) {}
    },
    haversineDistanceMiles(lat1Deg, lon1Deg, lat2Deg, lon2Deg) {
      //https://stackoverflow.com/questions/14560999/using-the-haversine-formula-in-javascript
      function toRad(degree) {
        return (degree * Math.PI) / 180;
      }

      const lat1 = toRad(lat1Deg);
      const lon1 = toRad(lon1Deg);
      const lat2 = toRad(lat2Deg);
      const lon2 = toRad(lon2Deg);

      const { sin, cos, sqrt, atan2 } = Math;

      const R = 3963; // earth radius in miles
      const dLat = lat2 - lat1;
      const dLon = lon2 - lon1;
      const a = sin(dLat / 2) * sin(dLat / 2) + cos(lat1) * cos(lat2) * sin(dLon / 2) * sin(dLon / 2);
      const c = 2 * atan2(sqrt(a), sqrt(1 - a));
      const d = R * c;
      return d; // distance in miles
    },
    scrolledPastObserver() {
      const wrapper = this.$refs.addProductWrapper;

      if (!wrapper) return;

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const { boundingClientRect, intersectionRatio, target } = entry;
            if (!(target instanceof HTMLElement)) continue;

            const isOutOfView = intersectionRatio === 0 && boundingClientRect.bottom <= 0;

            target.classList.toggle("add-product-fixed", isOutOfView);
            target.style.setProperty("--_computed-add-product-height", `${boundingClientRect.height}px`);

            if (isOutOfView) {
              target.style.setProperty("--_info-layout", " ");
              this.$refs.productDetailsSubscription?.style.setProperty("--_info-layout", " ");
            } else {
              target.style.removeProperty("--_info-layout");
              this.$refs.productDetailsSubscription?.style.removeProperty("--_info-layout");
            }
          }
        },
        {
          root: null,
          rootMargin: "0px",
          threshold: [0, 1],
        }
      );

      observer.observe(wrapper);
    },
  }));
});
